import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService, SsoLoginOptions } from 'src/app/shared/services/auth.service';
import { ErrorParserService } from 'src/app/shared/services/error-parcing.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { FormErrorMatcher } from 'src/app/shared/utils/form-error-matcher';
import { debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { SNACK_BAR_DURATION } from 'src/app/shared/models/data-catalogs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  loginInProgress: boolean = false;
  sso: SsoLoginOptions | null = null;

  matcher = new FormErrorMatcher();

  private ssoLookup: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private _snackBar: MatSnackBar,
    private errorService: ErrorParserService,
    private clipboard: Clipboard,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', { validators: [Validators.required, Validators.email]}],
      password: ['', { validators: Validators.required}]
    });

    this.ssoLookup = this.emailControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((email: string) =>
          this.emailControl.valid ? this.authService.ssoLoginOptions(email.trim()) : of(null))
      )
      .subscribe((options) => this.applySsoOptions(options));
  }

  ngOnDestroy(): void {
    if (this.ssoLookup) {
      this.ssoLookup.unsubscribe();
    }
  }

  get emailControl() {
    return this.loginForm.controls.email;
  }

  get passwordControl() {
    return this.loginForm.controls.password;
  }

  get ssoRequired(): boolean {
    return !!(this.sso && this.sso.ssoRequired);
  }

  get providerLabel(): string {
    return (this.sso && this.sso.providerName) || 'your organisation';
  }

  // An SSO-only domain has no password to ask for, so the field must stop being required.
  private applySsoOptions(options: SsoLoginOptions | null) {
    this.sso = options && options.ssoAvailable ? options : null;
    if (this.ssoRequired) {
      this.passwordControl.clearValidators();
    } else {
      this.passwordControl.setValidators(Validators.required);
    }
    this.passwordControl.updateValueAndValidity();
  }

  startSso() {
    const url = this.authService.ssoStartUrl(this.sso ? this.sso.startUrl : null);
    if (!url) {
      this._snackBar.open('Single sign-on is not available right now. Try again shortly.', null, { duration: SNACK_BAR_DURATION });
      return;
    }
    this.loginInProgress = true;
    window.location.assign(url);
  }

  login() {
    if (this.ssoRequired) {
      this.startSso();
      return;
    }
    if (this.loginForm.invalid) {
      return;
    }

    this.loginInProgress = true;
    let redirecting = false;
    this.authService.login(this.emailControl.value, this.passwordControl.value)
    .pipe(
      finalize(() => this.loginInProgress = redirecting)
    )
    .subscribe(
      (result) => {
        redirecting = result.redirecting;
        if (!redirecting) {
          this.router.navigate(['/home']);
        }
      },
      (error) => {
        console.error(error);
        if (error && error.userFacing) {
          this.dialog.open(AlertDialogComponent, {
            data: {
              type: 'error',
              message: error.message
            }
          });
        } else if (this.errorService.catchAuthError(error)) {
          this.dialog.open(AlertDialogComponent, {
            data: {
              type: 'error',
              message: 'Incorrect email or password!'
            }
          });
        } else {
          const errorString = this.errorService.parseError(error);
          if (errorString) {
            this._snackBar.open(
              `Error ID: ${errorString}`,
              'Copy',
              { duration: SNACK_BAR_DURATION })
            .onAction()
            .subscribe(() => {
              this.clipboard.copy(errorString);
            });
          } else {
            this._snackBar.open(`Login failed`, null, { duration: SNACK_BAR_DURATION });
          }
        }
      }
    );
  }

}
